import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  loadGroupData, BarCompareConstNat, generateWardMapImage,
  fetchConstituencyBoundsFromGeo, WardGroupStaticMapWithBase, ConstituencyGroupStaticMap,
  TopThreePrioritiesMini, getTopVI, generateComparisonBlurb,
} from "./utils/metricsClient";
import { BestPMGrid } from "./utils/metricsClient";
import { MPCard, loadFullMpData, DemographicBlock, fetchLatestElection } from "./utils/metricsClient";

/* ─── Design tokens ────────────────────────────────────────────── */
const INK        = "#0f172a";
const INK2       = "#64748b";
const INK3       = "#94a3b8";
const BORDER     = "#e2e8f0";
const SURF       = "#f8fafc";
const STACK_TEAL = "#0abfbc";
const STACK_DARK = "#2c2c2a";
const BLUE       = "#185fa5";
const VIOLET     = "#5b21b6";
const AMBER      = "#92400e";
const POS        = "#15803d";
const NEG        = "#b91c1c";
const POSBG      = "#dcfce7";
const NEGBG      = "#fee2e2";

const PARTY_COLORS = {
  "Conservative": "#0087DC", "Labour": "#D50000",
  "Liberal Democrat": "#FDBB30", "Reform UK": "#12B6CF", "Reform": "#12B6CF",
  "Green": "#008066", "SNP": "#FFF95D", "Plaid Cymru": "#3F8428", "Other": "#64748b",
};
const partyCol = (label) => PARTY_COLORS[label] || STACK_TEAL;

/* ─── Micro-components ───────────────────────────────────────── */
const SL = ({ children, color = STACK_TEAL }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mb: "4px", flexShrink: 0 }}>
    <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
    <Typography sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: STACK_DARK, whiteSpace: "nowrap" }}>
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: "0.5px", bgcolor: BORDER }} />
  </Box>
);
const Callout = ({ eyebrow, headline, sub, implication, accentColor }) => (
  <Box
    sx={{
      border: `1px solid ${BORDER}`,
      borderTop: `3px solid ${accentColor}`,
      borderRadius: "10px",
      p: "10px 11px",
      bgcolor: "#fff",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      height: "100%",
      boxSizing: "border-box",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    }}
  >
    <Typography
      sx={{
        fontSize: 7,
        fontWeight: 800,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: accentColor,
        mb: "4px",
        lineHeight: 1.1,
      }}
    >
      {eyebrow}
    </Typography>

    <Typography
      sx={{
        fontSize: 10.5,
        fontWeight: 750,
        lineHeight: 1.22,
        color: INK,
        mb: "5px",
        letterSpacing: "-0.01em",
      }}
    >
      {headline}
    </Typography>

    <Typography
      sx={{
        fontSize: 8.2,
        color: INK2,
        lineHeight: 1.32,
      }}
    >
      {sub}
    </Typography>

    {implication && (
      <Box
        sx={{
          mt: "7px",
          pt: "6px",
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Typography
          sx={{
            fontSize: 8,
            color: accentColor,
            lineHeight: 1.3,
            fontWeight: 650,
          }}
        >
          {implication}
        </Typography>
      </Box>
    )}
  </Box>
);

const SlimPill = ({ label, party, share, majority }) => {
  if (!party) return null;
  const col = partyCol(party);
  return (
    <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "4px",
      bgcolor: `${col}20`,
      border: `0.5px solid ${col}`,
      borderRadius: "10px",
      px: "8px",
      py: "4px",
      flexShrink: 0,
      minWidth: 120,
      justifyContent: "flex-start",
    }}
  >
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: col, flexShrink: 0 }} />
      <Box>
        <Typography sx={{ fontSize: 8, fontWeight: 600, color: INK3, lineHeight: 1, mb: "1px" }}>{label}</Typography>
        <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: INK, lineHeight: 1, whiteSpace: "nowrap" }}>
          {party}
          {share != null && <Box component="span" sx={{ fontSize: 8, fontWeight: 500, color: INK2 }}> · {Number(share).toFixed(1)}%</Box>}
          {majority != null && <Box component="span" sx={{ fontSize: 8, fontWeight: 500, color: INK2 }}> · Maj {Number(majority).toLocaleString()}</Box>}
        </Typography>
      </Box>
    </Box>
  );
};

const InlineHeader = ({ constName, areaCode, topVI }) => {
  const [latestElection, setLatestElection] = React.useState(null);
  const [winner2019, setWinner2019]         = React.useState(null);

  React.useEffect(() => {
    if (!areaCode) return;
    let cancelled = false;
    fetchLatestElection(areaCode).then(d => { if (!cancelled) setLatestElection(d); }).catch(() => {});
    const CSV_URL = process.env.PUBLIC_URL + "/static/data/Notional2019Winners_Newpcon.csv";
    fetch(CSV_URL).then(r => r.text()).then(text => {
      if (cancelled) return;
      const lines = text.trim().split("\n");
      if (lines.length < 2) return;
      const header = lines[0].replace(/^\uFEFF/, "").split(",");
      for (const line of lines.slice(1)) {
        const cols = line.split(",");
        const row = Object.fromEntries(header.map((k, i) => [k.trim(), cols[i]?.trim()]));
        if (row.new_pcon === areaCode) {
          const shares = {
            Conservative: Number(row.con19), Labour: Number(row.lab19),
            "Liberal Democrat": Number(row.ld19), Green: Number(row.green19),
            Reform: Number(row.brx19), "Plaid Cymru": Number(row.pc19),
            SNP: Number(row.snp19), Other: Number(row.other19),
          };
          // The CSV spells two winners differently from the keys above
          // ("LibDem", "PlaidCymru"), so `shares[row.winner]` was undefined and
          // the pill printed 0.0% — and the wrong colour — in the 11 seats
          // those two won in 2019. Map the label onto the canonical name once,
          // before it is used for either.
          const party2019 = { LibDem: "Liberal Democrat", PlaidCymru: "Plaid Cymru" }[row.winner] || row.winner;
          const party = party2019 || Object.entries(shares).reduce(
            (a, b) => (b[1] > (shares[a] || 0) ? b[0] : a), "Other"
          );
          setWinner2019({ party, share: (shares[party] || 0) * 100 });
          break;
        }
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [areaCode]);

  const accent = latestElection?.party ? partyCol(latestElection.party)
    : topVI?.label ? partyCol(topVI.label) : STACK_TEAL;

  return (
    <Box sx={{ pb: "5px", borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "2px" }}>
        <Box component="img" src={`${process.env.PUBLIC_URL}/static/images/stack_logo.png`} alt="Stack" sx={{ height: 13, opacity: 0.6 }} />
        <Typography sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: INK3 }}>
          Constituency Profile
        </Typography>
        <Typography sx={{ ml: "auto", fontSize: 8.5, color: INK3 }}>Prepared by Stack Data Strategy</Typography>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", alignItems: "center", columnGap: "8px", mb: "3px" }}>
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: INK, letterSpacing: "-0.5px", lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {constName}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <SlimPill label="VI Projection" party={topVI?.label} share={topVI?.constituency} />
          <SlimPill label="2024 Result"   party={latestElection?.party} share={latestElection?.share} majority={latestElection?.majority} />
          <SlimPill label="2019 Result"   party={winner2019?.party} share={winner2019?.share} />
        </Box>
      </Box>
      <Box sx={{ height: 3, width: 180, borderRadius: 4, background: `linear-gradient(90deg, ${accent}, ${accent}55)` }} />
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   
   Layout uses CSS grid rows with fr units inside the fixed A4 box.
   All chart components now self-measure via ResizeObserver (metricsClient
   changes) so they fill their grid cell - no hardcoded pixel heights here.

   Grid row template (inside 1099px inner height after 12px top+bot padding):
     "header"      - auto  (fits its content, ~48px)
     "mpcard"      - auto  (fits its content, ~100px; MPCard now height:100%)  
     "maps"        - 1fr   (first flexible row, ~160px)
     "callouts"    - auto  (content-driven callout boxes, ~105px)
     "demographics"- auto  (~62px)
     "charts"      - 2fr   (gets twice as much remaining space as maps)
   
   The "maps" 1fr and "charts" 2fr rows absorb all leftover space after the
   auto rows settle. This guarantees the page is always fully filled.
═══════════════════════════════════════════════════════════════ */

// Political questions that can occupy the report's "hero" slot. Maps each to
// the charts-state key it lives under and a display label.
const HERO_QUESTIONS = {
  vote_intention:  { k: "vi",         label: "Vote intention" },
  national_issues: { k: "national",   label: "Most important issues" },
  priorities:      { k: "priorities", label: "Local priorities" },
  best_pm:         { k: "best_pm",     label: "Best prime minister" },
};

// Some political questions (e.g. national_issues) ship no colours, so their
// bars/maps would render grey. Assign a distinct palette by key order when the
// data carries no real colour.
const HERO_PALETTE = ["#4e79a7", "#f28e2b", "#e15759", "#76b7b2", "#59a14f", "#edc948", "#b07aa1", "#ff9da7", "#9c755f", "#bab0ab"];
function ensureColors(data = []) {
  const hasColor = data.some((d) => d.color && !/^#8{3,6}$/i.test(d.color));
  if (hasColor) return data;
  return data.map((d, i) => ({ ...d, color: HERO_PALETTE[i % HERO_PALETTE.length] }));
}

const ConstituencyReportView = ({
  constName, mpName, party, roles, majority, mapImages, demographics, areaCode,
  height = "1123px",
  heroGroupId = "vote_intention",   // which question gets the highlight slot
  national = false,                 // true => whole-GB political report
}) => {
  const [charts, setCharts] = useState({ vi: [], national: [], priorities: [], best_pm: [], loading: true });
  const topVI = getTopVI(charts.vi);
  const prevArea = useRef(null);

  useEffect(() => {
    if (!areaCode || areaCode === prevArea.current) return;
    prevArea.current = areaCode;
    setCharts({ vi: [], national: [], priorities: [], loading: true });
    (async () => {
      try {
        const [vi, national, priorities, best_pm] = await Promise.all([
          loadGroupData({ groupId: "vote_intention",  areaCode, level: "constituency" }),
          loadGroupData({ groupId: "national_issues", areaCode, level: "constituency" }),
          loadGroupData({ groupId: "priorities",      areaCode, level: "constituency" }),
          loadGroupData({ groupId: "best_pm",         areaCode, level: "constituency" }),
        ]);
        setCharts({ vi, national, priorities, best_pm, loading: false });
      } catch { setCharts(s => ({ ...s, loading: false })); }
    })();
  }, [areaCode]);

  const [mpData, setMpData] = useState(null);
  useEffect(() => {
    if (!constName) return;
    loadFullMpData(constName).then(setMpData);
  }, [constName]);

  /* ── Derived intelligence ───────────────────────────────────── */
  const viData = charts.vi || [];

  const topVIParty = useMemo(() => [...viData]
    .filter(d => Number.isFinite(Number(d?.constituency)) && !String(d.key).includes("dontknow"))
    .sort((a, b) => Number(b.constituency) - Number(a.constituency))[0] || null, [viData]);

  const secondVIParty = useMemo(() => [...viData]
    .filter(d => Number.isFinite(Number(d?.constituency)) && !String(d.key).includes("dontknow"))
    .sort((a, b) => Number(b.constituency) - Number(a.constituency))[1] || null, [viData]);

  const viGap = useMemo(() =>
    topVIParty && secondVIParty
      ? +(Number(topVIParty.constituency) - Number(secondVIParty.constituency)).toFixed(1) : null,
  [topVIParty, secondVIParty]);

  const seatRisk = useMemo(() => {
    if (viGap == null) return null;
    if (viGap > 15) return { level: "Safe",          color: POS,        bg: POSBG };
    if (viGap > 7)  return { level: "Likely",         color: STACK_TEAL, bg: "#ccfbf1" };
    if (viGap > 3)  return { level: "Marginal",       color: AMBER,      bg: "#fef3c7" };
    return               { level: "Highly marginal", color: NEG,        bg: NEGBG };
  }, [viGap]);

  const topIssues = useMemo(() =>
    (charts.national || []).filter(d => Number.isFinite(Number(d?.constituency)))
      .sort((a, b) => Number(b.constituency) - Number(a.constituency)).slice(0, 3),
  [charts.national]);

  const topIssueAboveNat = useMemo(() =>
    (charts.national || [])
      .filter(d => Number.isFinite(Number(d?.constituency)) && Number.isFinite(Number(d?.national)))
      .map(d => ({ ...d, gap: +(Number(d.constituency) - Number(d.national)).toFixed(1) }))
      .sort((a, b) => b.gap - a.gap)[0] || null,
  [charts.national]);

  const noneAbove = useMemo(() =>
    (charts.best_pm || []).find(d => String(d.key || d.label || "").toLowerCase().includes("none")),
  [charts.best_pm]);

  const topPriorityAboveNat = useMemo(() => {
    const top3 = (charts.priorities || [])
      .filter(d => Number.isFinite(Number(d?.constituency)) && Math.abs(Number(d.constituency)) > 1e-6)
      .sort((a, b) => Number(b.constituency) - Number(a.constituency))
      .slice(0, 3);
    return top3
      .filter(d => Number.isFinite(Number(d?.national)))
      .map(d => ({ ...d, gap: +(Number(d.constituency) - Number(d.national)).toFixed(1) }))
      .sort((a, b) => b.gap - a.gap)[0] || null;
  }, [charts.priorities]);

  /* ── National variant ────────────────────────────────────────────
     Same structure as the seat report, at GB level: ward maps → GB
     constituency maps, values → national, no MP/seat-risk, national demographics. */
  if (national) {
    const nat = (arr) => (arr || []).map((x) => ({ ...x, constituency: (x.national ?? x.constituency), national: null }));
    const viN = nat(charts.vi), issuesN = nat(charts.national), prioN = nat(charts.priorities), pmN = nat(charts.best_pm);
    const topOf = (arr) => [...arr]
      .filter((d) => Number.isFinite(Number(d?.constituency)) && !String(d.key).includes("dontknow") && !String(d.key).toLowerCase().includes("none"))
      .sort((a, b) => Number(b.constituency) - Number(a.constituency))[0] || null;
    const topVIn = topOf(viN), topIssueN = topOf(issuesN), topPrioN = topOf(prioN);
    const pct = (v) => (Number.isFinite(Number(v)) ? `${Number(v).toFixed(1)}%` : "—");

    // Highlight swap (national): the promoted question takes VI's map+chart slot
    // in the maps row and VI drops into whichever bottom slot the hero vacated -
    // mirroring the constituency swap, but with GB constituency maps.
    const isSwap = heroGroupId !== "vote_intention" && !!HERO_QUESTIONS[heroGroupId];
    const heroMeta = HERO_QUESTIONS[heroGroupId] || HERO_QUESTIONS.vote_intention;
    const heroDataN = ensureColors(nat(charts[heroMeta.k] || []));
    const heroColors = Object.fromEntries(heroDataN.map((d) => [d.key, d.color]));

    const renderViSlotN = (maxRows = 8) => (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
        <SL color={BLUE}>Vote intention — national</SL>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <BarCompareConstNat data={viN} constName="Great Britain" hideNational maxRows={maxRows} />
        </Box>
      </Box>
    );

    return (
      <Box
        id="report-content"
        sx={{
          backgroundColor: "white", color: "black",
          width: "794px", height, overflow: "hidden",
          mx: "auto", p: "12px 18px",
          border: "1px solid #ccc", boxSizing: "border-box",
          display: "grid",
          gridTemplateRows: "auto 1.25fr auto auto 1.6fr",
          gridTemplateColumns: "1fr",
          gap: "7px",
        }}
      >
        {/* Header */}
        <Box sx={{ pb: "5px", borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "2px" }}>
            <Box component="img" src={`${process.env.PUBLIC_URL}/static/images/stack_logo.png`} alt="Stack" sx={{ height: 13, opacity: 0.6 }} />
            <Typography sx={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: INK3 }}>
              National Political Profile
            </Typography>
            <Typography sx={{ ml: "auto", fontSize: 8.5, color: INK3 }}>Prepared by Stack Data Strategy</Typography>
          </Box>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: INK, letterSpacing: "-0.5px", lineHeight: 1.3 }}>
            {constName || "Great Britain"}
          </Typography>
          <Typography sx={{ fontSize: 10, color: INK2, mt: "1px" }}>
            {topVIn ? `${topVIn.label} leads nationally on ${pct(topVIn.constituency)} vote intention` : "Vote intention & political landscape"}
          </Typography>
        </Box>

        {/* Maps + VI/hero chart. Swap → 2 cols (hero map + hero chart); default
            → 3 cols (past-vote map + VI chart + VI map). */}
        <Box sx={{ display: "grid", gridTemplateColumns: isSwap ? "1fr 1fr" : "1fr 1fr 1fr", gap: "6px", minHeight: 0 }}>
          {/* Left map: past vote (default) OR the hero's own map (swap) */}
          <Box sx={{ position: "relative", minHeight: 0, height: "100%" }}>
            <ConstituencyGroupStaticMap groupId={isSwap ? heroGroupId : "past_vote"} colors={isSwap ? heroColors : undefined} />
            <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, bgcolor: "rgba(255,255,255,0.92)", py: "3px", textAlign: "center", borderTop: `0.5px solid ${BORDER}`, pointerEvents: "none" }}>
              <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>{isSwap ? `${heroMeta.label} by Constituency` : "Past Vote by Constituency"}</Typography>
            </Box>
          </Box>
          {/* Centre chart: VI (default) OR the hero question (swap) */}
          <Box sx={{ minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <BarCompareConstNat data={isSwap ? heroDataN : viN} constName="Great Britain" hideNational maxRows={isSwap && heroGroupId === "national_issues" ? 6 : 10} />
            </Box>
            <Box sx={{ flexShrink: 0, textAlign: "center", bgcolor: "rgba(255,255,255,0.5)", py: "3px", borderTop: `0.5px solid ${BORDER}` }}>
              <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>{isSwap ? `National ${heroMeta.label}` : "National Vote Intention"}</Typography>
            </Box>
          </Box>
          {/* VI constituency map — default layout only */}
          {!isSwap && (
            <Box sx={{ position: "relative", minHeight: 0, height: "100%" }}>
              <ConstituencyGroupStaticMap groupId="vote_intention" />
              <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, bgcolor: "rgba(255,255,255,0.92)", py: "3px", textAlign: "center", borderTop: `0.5px solid ${BORDER}`, pointerEvents: "none" }}>
                <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>Voting Intention by Constituency</Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Callouts */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "8px", alignItems: "stretch" }}>
          <Callout
            eyebrow="Vote intention"
            accentColor={topVIn ? partyCol(topVIn.label) : STACK_TEAL}
            headline={topVIn ? `${topVIn.label} leads on ${pct(topVIn.constituency)}` : "Loading…"}
            sub={topVIn ? `${topVIn.label} is the strongest party nationally on projected vote intention.` : ""}
            implication="The national baseline against which every constituency can be benchmarked."
          />
          <Callout
            eyebrow="Issue landscape"
            accentColor={BLUE}
            headline={topIssueN ? `${topIssueN.label} tops the national agenda` : "Loading…"}
            sub={topIssueN ? `${topIssueN.label} is the most cited important issue across GB (${pct(topIssueN.constituency)}).` : ""}
            implication={topIssueN ? `${topIssueN.label} is the dominant frame for national engagement.` : null}
          />
          <Callout
            eyebrow="Priority signal"
            accentColor={STACK_TEAL}
            headline={topPrioN ? `${topPrioN.label} is the leading priority` : "Loading…"}
            sub={topPrioN ? `${topPrioN.label} comes through as the top local priority nationally (${pct(topPrioN.constituency)}).` : ""}
            implication={topPrioN ? `A credible national lead theme for public engagement.` : null}
          />
        </Box>

        {/* National demographics (same component as the seat report) */}
        <Box sx={{ flexShrink: 0 }}>
          <DemographicBlock national constName="Great Britain" />
        </Box>

        {/* Bottom charts. In swap mode VI fills whichever slot the hero left. */}
        <Box sx={{ display: "grid", gridTemplateColumns: "0.82fr 1fr", gap: "14px", minHeight: 0, height: "100%" }}>
          {isSwap && heroGroupId === "national_issues" ? renderViSlotN(8) : (
          <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
            <SL color={BLUE}>Most important issues — national</SL>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <BarCompareConstNat data={issuesN} constName="Great Britain" hideNational />
            </Box>
          </Box>
          )}
          <Box sx={{ display: "grid", gridTemplateRows: "1.5fr 1fr", gap: "14px", minHeight: 0, height: "100%" }}>
            {isSwap && heroGroupId === "best_pm" ? renderViSlotN(6) : (
            <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
              <SL color={VIOLET}>Best prime minister</SL>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <BestPMGrid data={pmN} showTitle={false} height="100%" hideNational />
              </Box>
            </Box>
            )}
            {isSwap && heroGroupId === "priorities" ? renderViSlotN(4) : (
            <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
              <SL color={STACK_TEAL}>Top priorities</SL>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <TopThreePrioritiesMini data={prioN} height="100%" hideNational />
              </Box>
            </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  /* ── Highlight swap ──────────────────────────────────────────────
     When a non-default question is promoted, the layout stays identical to
     the default report - the hero simply takes vote intention's maps-row slot
     (as map + chart, since non-VI questions have a single ward map), and VI
     drops into whichever bottom slot the hero vacated. */
  const isSwap = heroGroupId !== "vote_intention" && !!HERO_QUESTIONS[heroGroupId];
  const heroMeta = HERO_QUESTIONS[heroGroupId] || HERO_QUESTIONS.vote_intention;
  const heroData = ensureColors(charts[heroMeta.k] || []);
  const heroColors = Object.fromEntries(heroData.map((d) => [d.key, d.color]));

  // VI content rendered into the bottom slot the hero vacated. maxRows keeps
  // it readable in the smaller slots (e.g. the priorities slot).
  const renderViSlot = (maxRows = 8) => (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
      <SL color={BLUE}>Vote intention</SL>
      <Typography sx={{ fontSize: 8.5, color: INK2, mb: "4px", lineHeight: 1.35, flexShrink: 0 }}>
        Projected vote share versus the national picture.
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <BarCompareConstNat data={ensureColors(viData)} constName={constName} maxRows={maxRows} />
      </Box>
    </Box>
  );

  return (
    <Box
      id="report-content"
      sx={{
        backgroundColor: "white",
        color: "black",
        width: "794px",
        height,
        overflow: "hidden",
        mx: "auto",
        p: "12px 18px",
        border: "1px solid #ccc",
        boxSizing: "border-box",
        /* ── CSS grid: every row declared, auto rows fit content,
              fr rows share remaining space proportionally ── */
        display: "grid",
        gridTemplateRows: "auto auto 1fr auto auto 1.5fr",
        gridTemplateColumns: "1fr",
        gap: "6px",
      }}
    >

      {/* ── ROW 1: HEADER (auto) ───────────────────────────────── */}
      <InlineHeader constName={constName} areaCode={areaCode} topVI={topVI} />

      {/* ── ROW 2: MP CARD (auto) ──────────────────────────────── */}
      {/* MPCard now uses height:100% internally so it fills this cell */}
      <Box sx={{ minHeight: 0, overflow: "hidden" }}>
        <MPCard mpData={mpData} />
      </Box>

      {/* ── ROW 3: MAPS + VI CHART (1.1fr) ─────────────────────── */}
      {/*
          This row expands to fill its fr share.
          WardGroupStaticMapWithBase now self-measures via ResizeObserver -
          no height prop needed; it fills height:100% of its parent.
      */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: isSwap ? "1fr 1fr" : "1fr 1fr 1fr",
        gap: "6px",
        minHeight: 0,
      }}>

        {/* Left map: past vote (default) OR the hero's own ward map (swap) */}
        <Box sx={{ position: "relative", minHeight: 0, height: "100%" }}>
          <WardGroupStaticMapWithBase
            groupId={isSwap ? heroGroupId : "past_vote"}
            areaCode={areaCode}
            colors={isSwap ? heroColors : undefined}
          />
          <Box sx={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            bgcolor: "rgba(255,255,255,0.92)", py: "3px", textAlign: "center",
            borderTop: `0.5px solid ${BORDER}`, pointerEvents: "none",
          }}>
            <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>
              {isSwap ? `${heroMeta.label} by Ward` : "Past Vote by Ward"}
            </Typography>
          </Box>
        </Box>

        {/* Centre chart: VI (default) OR the hero question (swap) */}
        <Box sx={{ minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <BarCompareConstNat
              data={isSwap ? heroData : charts.vi}
              constName={constName}
              maxRows={isSwap && heroGroupId === "national_issues" ? 6 : 10}
              firstRowBadge={!isSwap && seatRisk && (
                <span style={{
                  display: "inline-flex",
                  alignItems: "center",
                  backgroundColor: seatRisk.bg,
                  border: `1px solid ${seatRisk.color}`,
                  borderRadius: 3,
                  padding: "1px 6px",
                  fontSize: 8,
                  fontWeight: 700,
                  color: seatRisk.color,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  lineHeight: 1.5,
                }}>
                  {seatRisk.level} seat
                </span>
              )}
            />
          </Box>
          <Box sx={{
            flexShrink: 0,
            textAlign: "center", bgcolor: "rgba(255,255,255,0.5)", py: "3px",
            borderTop: `0.5px solid ${BORDER}`,
          }}>
            <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>
              {`${constName || "Constituency"} ${isSwap ? heroMeta.label : "Vote Intention"} vs National`}
            </Typography>
          </Box>
        </Box>

        {/* VI ward map - only in the default (non-swap) layout */}
        {!isSwap && (
          <Box sx={{ position: "relative", minHeight: 0, height: "100%" }}>
            <WardGroupStaticMapWithBase groupId="vote_intention" areaCode={areaCode} />
            <Box sx={{
              position: "absolute", bottom: 0, left: 0, right: 0,
              bgcolor: "rgba(255,255,255,0.92)", py: "3px", textAlign: "center",
              borderTop: `0.5px solid ${BORDER}`, pointerEvents: "none",
            }}>
              <Typography sx={{ fontSize: 9, fontWeight: 600, color: INK2 }}>Voting Intention by Ward</Typography>
            </Box>
          </Box>
        )}
      </Box>

          {/* ── ROW 4: CALLOUTS (auto) ─────────────────────────────── */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "8px", alignItems: "stretch" }}>
        <Callout
          eyebrow="Seat dynamics"
          accentColor={seatRisk?.color || STACK_TEAL}
          headline={(() => {
            if (!seatRisk || !topVIParty) return "Loading…";
            return `${topVIParty.label} leads in a ${seatRisk.level.toLowerCase()} seat`;
          })()}
          sub={(() => {
            if (!topVIParty || !secondVIParty) return "";
            return `${Number(topVIParty.constituency).toFixed(1)}% for ${topVIParty.label} against ${Number(secondVIParty.constituency).toFixed(1)}% for ${secondVIParty.label}${majority ? `; 2024 majority ${Number(majority).toLocaleString()}` : ""}.`;
          })()}
          implication={(() => {
            if (!seatRisk) return null;
            if (seatRisk.level === "Safe") {
              return `Public affairs engagement can lean into policy depth rather than electoral defensiveness.`;
            }
            if (seatRisk.level === "Likely") {
              return `This is competitive enough that strong local evidence should still land.`;
            }
            if (seatRisk.level === "Marginal") {
              return `Local salience matters here - constituency proof points will carry weight.`;
            }
            return `Pressure is high in this seat, so campaigns need visible local relevance.`;
          })()}
        />

        <Callout
          eyebrow="Issue landscape"
          accentColor={BLUE}
          headline={(() => {
            if (!topIssueAboveNat) return topIssues[0] ? `${topIssues[0].label} leads locally` : "Loading…";
            return `${topIssueAboveNat.label} over-indexes most strongly`;
          })()}
          sub={(() => {
            if (!topIssueAboveNat) return "";
            const gap = topIssueAboveNat.gap;
            return `${Number(topIssueAboveNat.constituency).toFixed(1)}% here versus ${Number(topIssueAboveNat.national).toFixed(1)}% nationally (${gap > 0 ? "+" : ""}${gap}pp).`;
          })()}
          implication={(() => {
            if (!topIssueAboveNat) return null;
            return `${topIssueAboveNat.label} is the clearest route into the local conversation.`;
          })()}
        />

        <Callout
          eyebrow="Priority signal"
          accentColor={STACK_TEAL}
          headline={(() => {
            if (!topPriorityAboveNat) {
              const top = (charts.priorities || [])
                .filter(d => Number.isFinite(Number(d?.constituency)))
                .sort((a, b) => Number(b.constituency) - Number(a.constituency))[0];
              return top ? `${top.label} comes through as the top priority` : "Loading…";
            }
            return `${topPriorityAboveNat.label} stands out in this constituency`;
          })()}
          sub={(() => {
            if (!topPriorityAboveNat) return "";
            return `${Number(topPriorityAboveNat.constituency).toFixed(1)}% locally versus ${Number(topPriorityAboveNat.national).toFixed(1)}% nationally (+${topPriorityAboveNat.gap}pp).`;
          })()}
          implication={(() => {
            if (!topPriorityAboveNat) return null;
            if (topPriorityAboveNat.gap > 8) {
              return `This is a standout local priority and a strong lead frame for engagement.`;
            }
            if (topPriorityAboveNat.gap > 3) {
              return `This priority has more cut-through here than nationally.`;
            }
            return `This priority is still credible locally, even without major divergence from the national picture.`;
          })()}
        />
      </Box>

      {/* ── ROW 5: DEMOGRAPHICS (auto) ─────────────────────────── */}
      <Box sx={{ flexShrink: 0 }}>
        <DemographicBlock constCode={areaCode} constName={constName} />
      </Box>

      {/* ── ROW 6: BOTTOM CHARTS (2fr) ─────────────────────────── */}
      {/*
          Gets 2× the space of the maps row.
          Left: Issues BarCompareConstNat - self-measures, fills its flex cell.
          Right: Best PM (top half) + Local Priorities (bottom half) via grid.
          All children use height:100% / minHeight:0 to participate in flex.
      */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: "0.82fr 1fr",
        gap: "14px",
        minHeight: 0,
        height: "100%",
      }}>

        {/* LEFT: Issues bar chart - or VI if Issues is the promoted hero */}
        {isSwap && heroGroupId === "national_issues" ? renderViSlot(8) : (
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
          <SL color={BLUE}>Most important issues - local vs national</SL>
          <Typography sx={{ fontSize: 8.5, color: INK2, mb: "6px", lineHeight: 1.35, flexShrink: 0 }}>
            Above-national bars show where local voters diverge from the national mood - the policy levers with greatest local political resonance.
          </Typography>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <BarCompareConstNat data={charts.national} constName={constName} />
          </Box>
          {/* Annotation sits cleanly below the chart, never overlapping bars */}
          <Box sx={{
            flexShrink: 0,
            mt: "6px",
            p: "6px 9px",
            bgcolor: "#f8fafc",
            border: `0.5px solid ${BORDER}`,
            borderLeft: `3px solid ${BLUE}`,
            borderRadius: "3px",
          }}>
            <Typography sx={{ fontSize: 10, color: INK, lineHeight: 1.4, fontWeight: 500 }}>
              {generateComparisonBlurb(charts.national, constName)}
            </Typography>
          </Box>
        </Box>
        )}

        {/* RIGHT: Best PM (top) + Priorities (bottom) */}
        <Box sx={{
          display: "grid",
          gridTemplateRows: "1.5fr 1fr",
          gap: "14px",
          minHeight: 0,
          height: "100%",
        }}>

          {/* Best PM - or VI if Best PM is the promoted hero */}
          {isSwap && heroGroupId === "best_pm" ? renderViSlot(6) : (
          <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
            <SL color={VIOLET}>Best prime minister</SL>
            <Typography sx={{ fontSize: 8.5, color: INK2, mb: "4px", lineHeight: 1.35, flexShrink: 0 }}>
              Leadership preferences signal political mood and partisan lean.
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <BestPMGrid data={charts.best_pm} showTitle={false} height="100%" />
            </Box>
          </Box>
          )}

          {/* Local Priorities - or VI if Priorities is the promoted hero */}
          {isSwap && heroGroupId === "priorities" ? renderViSlot(4) : (
          <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
            <SL color={STACK_TEAL}>Local priorities</SL>
            <Typography sx={{ fontSize: 8.5, color: INK2, mb: "4px", lineHeight: 1.35, flexShrink: 0 }}>
              What this constituency prioritises
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <TopThreePrioritiesMini data={charts.priorities} height="100%" />
            </Box>
          </Box>
          )}

        </Box>
      </Box>

    </Box>
  );
};

export default ConstituencyReportView;